import { nullIfEmpty, parseJsonObject, readObject, readString } from './json';

/**
 * The served `GET /api/config` payload (RA-C10), as the Cloudflare Function
 * actually builds it (`quest-client/functions/api/config.js` spreading the KV
 * `client_config` - the wire truth). Port of `AssistProtocol.cs`
 * `AssistRemoteConfig`.
 *
 *   version 2 -> { version:2, server, livekitUrl, room, deployment, assist:{ autoAllow, ... }, wearerToken?, transport?, client:{ ... } }
 *   version 1 -> { version:1, server }   (no client_config published yet)
 *
 * Unknown fields are ignored; absent fields keep defaults (additive rule).
 */
export interface AssistRemoteConfig {
  readonly version: number;
  /** The legacy ws(s) signaling URL (old transport; may embed `?token=` - the wearer-gate secret's second home). */
  readonly server: string;
  readonly livekitUrl: string;
  readonly room: string;
  readonly deployment: string;
  /** The assist block's autoAllow (D6); null when the config has no opinion. */
  readonly assistAutoAllow: boolean | null;
  /** The published wearer-gate secret, when present in the config. */
  readonly wearerToken: string;
  /** Optional transport hint mirroring the server's QVS_TRANSPORT ("livekit" | "aiortc"); empty when unset. */
  readonly transport: string;
  /** Optional absolute token-endpoint override; empty = derive from the config URL. */
  readonly tokenEndpoint: string;
  /**
   * The assist join code, when this deployment stores one (D27); empty
   * otherwise. Empty is a normal answer, not a fault: a deployment may store
   * only the code's hash, and an expired code is never served.
   */
  readonly assistCode: string;
  /** When the join code expires (ISO-8601 as served); empty when unknown. */
  readonly assistCodeExpiresAt: string;
}

/** Parse a served config body. Null only for non-object/malformed JSON. */
export function parseAssistRemoteConfig(json: string): AssistRemoteConfig | null {
  const root = parseJsonObject(json);
  if (!root) return null;

  const assist = readObject(root, 'assist');
  const autoAllow = assist && typeof assist['autoAllow'] === 'boolean' ? (assist['autoAllow'] as boolean) : null;

  return {
    version: typeof root['version'] === 'number' && Number.isInteger(root['version']) ? (root['version'] as number) : 1,
    server: readString(root, 'server'),
    livekitUrl: readString(root, 'livekitUrl'),
    room: readString(root, 'room'),
    deployment: readString(root, 'deployment'),
    assistAutoAllow: autoAllow,
    wearerToken: readString(root, 'wearerToken'),
    transport: readString(root, 'transport'),
    tokenEndpoint: readString(root, 'tokenEndpoint'),
    assistCode: readString(root, 'assistCode'),
    assistCodeExpiresAt: readString(root, 'assistCodeExpiresAt'),
  };
}

/** Extract `?token=` from a ws/wss/http URL (the existing wearer token model). */
export function tokenFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const index = url.indexOf('token=');
  if (index < 0) return null;
  if (index > 0 && url[index - 1] !== '?' && url[index - 1] !== '&') return null;
  let value = url.slice(index + 'token='.length);
  const cut = value.search(/[&# ]/);
  if (cut >= 0) value = value.slice(0, cut);
  if (value.length === 0) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * The wearer-gate secret for `POST /api/token`, mirroring the Function's own
 * precedence (`resolveWearerSecret`): config `wearerToken` -> the legacy
 * server URL's `?token=` -> the device's configured fallback. Null when
 * nowhere supplies one.
 */
export function resolveWearerSecret(
  config: AssistRemoteConfig | null,
  deviceFallback: string | null | undefined,
): string | null {
  if (config?.wearerToken) return config.wearerToken;
  const fromUrl = tokenFromUrl(config?.server);
  if (fromUrl) return fromUrl;
  return deviceFallback ? deviceFallback : null;
}

function parseHttpUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * The token endpoint for a config endpoint: an explicit `tokenEndpoint` wins;
 * otherwise the sibling `/api/token` on the config URL's origin (the Pages
 * project serves both). Null when neither is derivable.
 */
export function resolveTokenEndpoint(config: AssistRemoteConfig | null, configUrl: string): string | null {
  if (config?.tokenEndpoint) {
    try {
      // Any absolute URL passes through, matching the Unity port's Uri check.
      new URL(config.tokenEndpoint);
      return config.tokenEndpoint;
    } catch {
      /* fall through to derivation */
    }
  }
  const uri = parseHttpUrl(configUrl);
  if (!uri) return null;
  return `${uri.protocol}//${uri.host}/api/token`;
}

/**
 * Normalize a manual-entry input into a config endpoint (RA-C10): an absolute
 * http(s) URL passes through; a bare host becomes
 * `https://<host>/api/config`. Null when the input is unusable.
 */
export function normalizeConfigEndpoint(input: string | null | undefined): string | null {
  if (!input || !input.trim()) return null;
  const value = input.trim();

  const absolute = parseHttpUrl(value);
  if (absolute) return absolute.toString();
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null; // absolute, but not http(s)

  // Bare host (or host/path fragment): host only, https, default path.
  let host = value;
  const cut = host.search(/[/?#:]/);
  if (cut >= 0) host = host.slice(0, cut);
  if (host.length === 0 || host.includes(' ')) return null;
  return `https://${host}/api/config`;
}

/**
 * The wearer token exchange (RA-C10 / D8, `POST /api/token` -
 * `quest-client/functions/api/token.js` is the wire truth): request
 * `{ role:"wearer", device?, cell? }` with the gate secret as
 * `Authorization: Bearer`; response
 * `{ token, identity, livekitUrl, room, deployment, expiresAt }`.
 */
export interface WearerTokenResponse {
  readonly token: string;
  readonly identity: string;
  readonly livekitUrl: string;
  readonly room: string;
  readonly deployment: string;
  /**
   * ISO-8601 as served. Read as text on purpose: the C# port carries a
   * Newtonsoft date-coercion workaround here; `JSON.parse` has no such
   * behaviour, so there is nothing to work around in TS.
   */
  readonly expiresAt: string;
}

/** Build the request body. Cell omitted when empty (the Function defaults it to "main"). */
export function wearerTokenRequestJson(device: string | null | undefined, cell?: string | null): string {
  const body: Record<string, string> = { role: 'wearer' };
  if (device) body['device'] = device;
  if (cell) body['cell'] = cell;
  return JSON.stringify(body);
}

/** Parse the response body. Null without a usable token. */
export function parseWearerTokenResponse(json: string): WearerTokenResponse | null {
  const root = parseJsonObject(json);
  if (!root) return null;
  const token = nullIfEmpty(readString(root, 'token'));
  if (!token) return null;
  return {
    token,
    identity: readString(root, 'identity'),
    livekitUrl: readString(root, 'livekitUrl'),
    room: readString(root, 'room'),
    deployment: readString(root, 'deployment'),
    expiresAt: readString(root, 'expiresAt'),
  };
}
